import { Avatar, Style } from '@dicebear/core'
import funEmoji from '@dicebear/styles/fun-emoji.json'

/**
 * Customer avatars, DiceBear "Fun Emoji" by Davis Uche (CC BY 4.0 — see ATTRIBUTION.md).
 *
 * Generated in the browser rather than fetched from api.dicebear.com. The seed is a customer
 * identifier, and the hosted API would mean sending one to a third party on every render of
 * every conversation in the list — a steady outbound trickle of who we are talking to, for a
 * decorative image. Local generation is also instant, works offline, and needs no CSP exception.
 *
 * Agents keep their initials. The avatar's job here is to make one customer distinguishable from
 * the next at a glance in the inbox, and giving everyone a face would undo that.
 */

const style = new Style(funEmoji)

// Same seed always yields the same avatar, so the SVG only has to be built once per customer.
// Without the cache the inbox rebuilds every avatar on each filter keystroke.
const cache = new Map<string, string>()

function avatarUri(seed: string, background: string): string {
  const key = `${seed}|${background}`
  const hit = cache.get(key)
  if (hit) return hit
  const uri = new Avatar(style, {
    seed,
    // DiceBear wants bare hex. Keeping the customer's existing colour means the inbox palette
    // does not change — only what sits on top of it.
    backgroundColor: [background.replace('#', '')],
  }).toDataUri()
  cache.set(key, uri)
  return uri
}

/**
 * Read from the style pack itself rather than hardcoded, so the credit cannot drift out of date
 * if the pack is updated or swapped — a stale attribution is a licence breach, not a typo.
 */
export const AVATAR_CREDIT = {
  creator: funEmoji.meta.creator.name,
  creatorUrl: funEmoji.meta.creator.url,
  license: funEmoji.meta.license.name,
  licenseUrl: funEmoji.meta.license.url,
  sourceUrl: funEmoji.meta.source.url,
}

export function CustomerAvatar({
  /** Stable per customer — the id, never the name: two people called Maria must not collide. */
  seed,
  color,
  name,
  size = 34,
  className = '',
}: {
  seed: string
  color: string
  name: string
  size?: number
  className?: string
}) {
  return (
    <img
      className={`avatar-img ${className}`}
      src={avatarUri(seed, color)}
      alt={name}
      width={size}
      height={size}
      style={{ width: size, height: size }}
    />
  )
}
